import { mapProduct } from 'helpers/algolia/map-product';
import { PRODUCT_RANGES } from 'helpers/constants/collection-names';
import { useParams } from 'next/navigation';
import React, { useEffect, useRef } from 'react';
import { useStats, useHits } from 'react-instantsearch-hooks-web';

interface Props {
  setNumberOfHits?: any;
  setProductHits?: any;
  setProductQuery?: any;
  searchQuery?: string;
}
const CustomStats: React.FC<Props> = ({ setNumberOfHits, setProductHits, setProductQuery, searchQuery }) => {
  const { locale } = useParams();

  const { nbHits } = useStats();
  const { hits, results } = useHits();
  // The search term this component has already produced a content term for.
  const derivedFor = useRef<string | null>(null);

  // Reported only once the query has actually resolved, so consumers can tell "no results" (0) from
  // "not known yet" (undefined). The content-term fallback depends on exactly that distinction.
  useEffect(() => {
    if (!results || results.__isArtificial) return;
    if (setNumberOfHits) setNumberOfHits(nbHits);
    if (setProductHits) {
        setProductHits(hits);
      }
  }, [nbHits, results]);

  // Derives the CONTENT search term from the best-matching product (its collection/range, else its
  // category), so content suggestions relate to what the products query actually matched.
  //
  // It stays `undefined` until the products query has genuinely resolved. That distinction matters: the
  // consumer only mounts the content index once this is defined, so the content index is queried ONCE
  // with the final term. Reporting '' early (as this used to) made it query with the raw search term and
  // then immediately re-query with the derived one — two content calls per search, the first discarded.
  useEffect(() => {
    if (!setProductQuery || !searchQuery) return;
    // `__isArtificial` is the placeholder our cached search client returns before Configure has supplied
    // a query/filters, so hits are not meaningful yet.
    if (!results || results.__isArtificial) return;
    // The results must belong to the CURRENT term. Two ways they might not: the idle no-match sentinel
    // query, which returns a real but EMPTY result set, or a still-in-flight response for the previous
    // term. Either one would otherwise latch the derived term to '' below and never re-derive.
    if (results.query !== searchQuery) return;
    // Derive ONCE per search term. Two reasons this must not re-run on every new `hits` identity:
    //   1. Each change of the derived term re-configures the content index, and adding/changing a widget
    //      makes InstantSearch run another search cycle — which re-queries products with identical params
    //      and hands back a fresh `hits` array, i.e. a feedback loop.
    //   2. `mapProduct` MUTATES the hit it inspects (setMasterVariantToChromeVariant assigns
    //      masterVariantID), so re-deriving over the same hits can pick a different product and yield a
    //      different term — observed flapping between two categories for one search.
    if (derivedFor.current === searchQuery) return;
    derivedFor.current = searchQuery;
    {
      setProductQuery('');
      if (hits?.length > 0) {
        const matchedProduct = hits?.find((hit) => {
          const mappedProduct = mapProduct(hit, locale, searchQuery);
          return mappedProduct?.selectedVariant;
        });
        if (matchedProduct) {
          const result = mapProduct(matchedProduct, locale, searchQuery);
          const range = result?.attributesAlgolia?.range?.toLowerCase();
          const productRanges = PRODUCT_RANGES.map((productRange) => productRange.toLowerCase());
          if (productRanges.includes(range)) {
            // Set product query for collections
            setProductQuery(result?.attributesAlgolia?.range);
          } else {
            // Set product query for categories
            setProductQuery(result?.categories?.[result?.categories?.length - 1]?.categoryId ?? '');
          }
        }
      } else {
        setProductQuery('');
      }
    }
  }, [searchQuery, setProductQuery, hits, results]);


  return <></>;
};

export default CustomStats;
